import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDatepicker, MatDateRangePicker } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { SatDatepickerInputDirective } from '../../directive/sat-datepicker-input.directive';

export type SatDateRangeValue = { begin: any; end: any } | '';

@Component({
	selector: 'sat-datepicker',
	templateUrl: './sat-datepicker.component.html',
	styleUrls: ['./sat-datepicker.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SatDatepickerComponent implements OnInit, OnDestroy {
	private static readonly WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;
	private static readonly RUNTIME_STYLE_ID = 'sat-datepicker-runtime-style';

	@Input() rangeMode = false;

	@ViewChild('dtPicker', { static: false }) dtPicker?: MatDateRangePicker<any>;
	@ViewChild('singlePicker', { static: false }) singlePicker?: MatDatepicker<any>;

	rangeGroup = new UntypedFormGroup({
		start: new UntypedFormControl(null),
		end: new UntypedFormControl(null),
	});
	singleControl = new UntypedFormControl(null);
	opened = false;

	private input?: SatDatepickerInputDirective;
	private readonly destroyed$ = new Subject<void>();
	private programmaticPatch = false;
	private lastEmittedRange: SatDateRangeValue = '';

	constructor(private readonly cdr: ChangeDetectorRef) {}

	_markForCheck(): void {
		this.cdr.markForCheck();
	}

	ngOnInit(): void {
		this.ensureRuntimeStyles();

		this.rangeGroup.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe({
			next: (value: any) => {
				if (!this.rangeMode || this.programmaticPatch) return;

				const begin = value?.start ?? null;
				const end = value?.end ?? null;

				if (begin && end) {
					const isAdjustingStartOnly =
						this.lastEmittedRange !== '' &&
						this.opened &&
						this.lastEmittedRange.begin !== begin &&
						this.lastEmittedRange.end === end;

					if (isAdjustingStartOnly) {
						this.programmaticPatch = true;
						this.rangeGroup.patchValue({ start: begin, end: null }, { emitEvent: false });
						this.programmaticPatch = false;
						return;
					}

					this.lastEmittedRange = { begin, end };
					this.input?.onPickerSelectionChange({ begin, end });
				} else if (!begin && !end) {
					this.lastEmittedRange = '';
					this.input?.onPickerSelectionChange('');
				}
			}
		});

		this.singleControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe({
			next: (value: any) => {
				if (this.rangeMode || this.programmaticPatch) return;
				this.input?.onPickerSelectionChange(value ?? null);
			}
		});
	}

	get disabled(): boolean {
		return this.input?.disabled ?? false;
	}

	_registerInput(input: SatDatepickerInputDirective): void {
		this.input = input;
	}

	_setValue(value: any): void {
		this.programmaticPatch = true;

		if (this.rangeMode) {
			const begin = value?.begin ?? null;
			const end = value?.end ?? null;
			this.rangeGroup.patchValue({ start: begin, end }, { emitEvent: false });
			this.lastEmittedRange = begin && end ? { begin, end } : '';
		} else {
			this.singleControl.setValue(value ?? null, { emitEvent: false });
		}

		this.programmaticPatch = false;
	}

	onPickerOpened(): void {
		this.opened = true;
		this.ensureRuntimeStyles();
		this.scheduleCalendarDomPatch();
	}

	onPickerClosed(): void {
		this.opened = false;
	}

	open(): void {
		this.openByMode();
	}

	private applyWeekdayLabels(): void {
		const pickerContents = Array.from(
			document.querySelectorAll<HTMLElement>('.mat-datepicker-content, .mat-date-range-picker-content'),
		);

		pickerContents.forEach((content) => {
			const hiddenNodes = Array.from(content.querySelectorAll<HTMLElement>('.cdk-visually-hidden'));
			hiddenNodes.forEach((node) => {
				node.style.setProperty('display', 'none', 'important');
			});
		});

		const calendars = Array.from(document.querySelectorAll<HTMLElement>('.mat-calendar'));

		calendars.forEach((calendar) => {
			const cells = Array.from(
				calendar.querySelectorAll<HTMLElement>('.mat-calendar-table-header tr:first-child th'),
			);

			cells.forEach((cell, index) => {
				const label = SatDatepickerComponent.WEEKDAY_LABELS[index];
				if (!label) return;

				const srOnly = cell.querySelector<HTMLElement>('.cdk-visually-hidden');
				if (srOnly) {
					srOnly.style.display = 'none';
				}

				for (const node of Array.from(cell.childNodes)) {
					if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim()) {
						node.remove();
					}
				}

				const visibleLabelNode = cell.querySelector<HTMLElement>('[aria-hidden="true"]');
				if (visibleLabelNode) {
					visibleLabelNode.textContent = label;
				} else {
					cell.insertBefore(document.createTextNode(label), cell.firstChild);
				}
			});
		});
	}

	private ensureRuntimeStyles(): void {
		if (document.getElementById(SatDatepickerComponent.RUNTIME_STYLE_ID)) return;

		const style = document.createElement('style');
		style.id = SatDatepickerComponent.RUNTIME_STYLE_ID;
		style.textContent = `
			.mat-datepicker-content .cdk-visually-hidden,
			.mat-date-range-picker-content .cdk-visually-hidden {
				display: none !important;
			}

			.mat-datepicker-content .mat-calendar-body-cell-content,
			.mat-date-range-picker-content .mat-calendar-body-cell-content,
			.mat-datepicker-content .mat-calendar-body-selected,
			.mat-date-range-picker-content .mat-calendar-body-selected {
				box-sizing: border-box !important;
				display: grid !important;
				place-items: center !important;
				min-width: 36px !important;
				min-height: 36px !important;
				margin: 0 auto !important;
				text-align: center !important;
				padding: 0 !important;
				line-height: 36px !important;
				text-indent: 0 !important;
			}
		`;

		document.head.appendChild(style);
	}

	private scheduleCalendarDomPatch(attempt = 0): void {
		this.applyWeekdayLabels();

		if (!this.opened || attempt >= 6) return;
		setTimeout(() => this.scheduleCalendarDomPatch(attempt + 1), 50);
	}

	private openByMode(attempt = 0): void {
		this.cdr.detectChanges();
		const picker = this.rangeMode ? this.dtPicker : this.singlePicker;
		if (picker) {
			picker.open();
			return;
		}

		if (attempt >= 20) return;
		setTimeout(() => this.openByMode(attempt + 1), 50);
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}
}
